import { infinityCards } from "../../../@shared/game/infinityCards";
import whisperingForestSource from "./whispering-forest-source.json";

export const WHISPERING_FOREST_STORY_PACK_CHEST_MODE = "story-random-pack";
export const WHISPERING_FOREST_STORY_PACK_REWARD_EVENT =
  "whispering-forest:story-pack-reward";

export const WHISPERING_FOREST_STORY_PACK_REWARD_IDS = Object.freeze([
  "08ahgmfrUXkqjuZrw5YW",
  "1t9ruhC7mRj5vVhqyzjO",
  "3TW3qkAKIOrhY2iGsCRq",
  "4XYtsS8XCnoMDWGZO1dL",
  "4igatDDkNcM2FN5N4iBM",
  "CMigdt1VIX3kygZeea0H",
  "E3V9pe7yNhajAK1nhQJ2",
  "Fki1kjjWd2nYbaC5sXww",
  "NquvLD7GdkIi6L2L7tUd",
  "O4yQTGdOjA1s6hnI1P4d",
  "QbbQAhH9rT9zTRKpgvAj",
  "R3HylH1GS1orDq3bqN5Q",
  "V8AFiagDbeR8W44hweO1",
]);

export const WHISPERING_FOREST_STORY_PACK_REWARD_POOL = Object.freeze(
  infinityCards.filter((card) =>
    WHISPERING_FOREST_STORY_PACK_REWARD_IDS.includes(String(card?.id || "")),
  ),
);
export const WHISPERING_FOREST_STORY_PACK_BOUNDS = Object.freeze({
  minX: 48,
  maxX: Math.max(
    48,
    whisperingForestSource.width * whisperingForestSource.tilewidth - 48,
  ),
  minY: 48,
  maxY: Math.max(
    48,
    whisperingForestSource.height * whisperingForestSource.tileheight - 48,
  ),
});

const CHEST_SPRITE_SIZE = Object.freeze({ width: 32, height: 31 });
const CHEST_SENSOR_PADDING = Object.freeze({ x: 6, y: 5 });
const CHEST_SENSOR_SIZE = Object.freeze({ width: 44, height: 41 });
const CHEST_CANDIDATE_STEP = 48;
export const STORY_PACK_CHEST_MIN_COUNT = 3;
export const STORY_PACK_CHEST_MAX_COUNT = 5;
export const STORY_PACK_CHEST_MIN_SPACING = 160;
const CHEST_ENTRY_EXCLUSION_RADIUS = 112;
const CHEST_RUNE_STONE_EXCLUSION_RADIUS = 96;
const CHEST_NPC_EXCLUSION_RADIUS = 84;
const CHEST_ZONE_PADDING = 56;
const CHEST_SAMPLE_OFFSETS = Object.freeze([
  { x: 0, y: 0 },
  { x: -22, y: -20 },
  { x: 22, y: -20 },
  { x: -22, y: 20 },
  { x: 22, y: 20 },
  { x: -22, y: 0 },
  { x: 22, y: 0 },
  { x: 0, y: -20 },
  { x: 0, y: 20 },
]);

let whisperingForestStoryChestVisit = null;

const defaultBlockedCheck = () => false;

const normalizeRect = (rect = null) => {
  const x = Number(rect?.x);
  const y = Number(rect?.y);
  const width = Number(rect?.width);
  const height = Number(rect?.height);
  if (
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(width) ||
    !Number.isFinite(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  return { x, y, width, height };
};

const normalizePoint = (point = null) => {
  const x = Number(point?.x);
  const y = Number(point?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) {
    return null;
  }

  return { x, y };
};

const randomIntInclusive = (min, max, random = Math.random) => {
  const safeMin = Math.ceil(Math.min(min, max));
  const safeMax = Math.floor(Math.max(min, max));
  return safeMin + Math.floor(random() * (safeMax - safeMin + 1));
};

const shuffle = (items = [], random = Math.random) => {
  const next = [...items];
  for (let index = next.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
  }
  return next;
};

const buildExpandedRect = (rect = null, padding = 0) => {
  const normalizedRect = normalizeRect(rect);
  if (!normalizedRect) return null;

  return {
    x: normalizedRect.x - padding,
    y: normalizedRect.y - padding,
    width: normalizedRect.width + padding * 2,
    height: normalizedRect.height + padding * 2,
  };
};

const rectsOverlap = (left = null, right = null) => {
  const rectA = normalizeRect(left);
  const rectB = normalizeRect(right);
  if (!rectA || !rectB) return false;

  return !(
    rectA.x + rectA.width <= rectB.x ||
    rectB.x + rectB.width <= rectA.x ||
    rectA.y + rectA.height <= rectB.y ||
    rectB.y + rectB.height <= rectA.y
  );
};

const distanceSquared = (left = null, right = null) => {
  const pointA = normalizePoint(left);
  const pointB = normalizePoint(right);
  if (!pointA || !pointB) return Number.POSITIVE_INFINITY;

  return (pointA.x - pointB.x) ** 2 + (pointA.y - pointB.y) ** 2;
};

const pointInsideRect = (point = null, rect = null) => {
  const normalizedPoint = normalizePoint(point);
  const normalizedRect = normalizeRect(rect);
  if (!normalizedPoint || !normalizedRect) return false;

  return (
    normalizedPoint.x >= normalizedRect.x &&
    normalizedPoint.x <= normalizedRect.x + normalizedRect.width &&
    normalizedPoint.y >= normalizedRect.y &&
    normalizedPoint.y <= normalizedRect.y + normalizedRect.height
  );
};

const buildChestSensorRect = (chest = null) => {
  const normalizedChest = normalizeRect({
    x: chest?.x,
    y: chest?.y,
    width: CHEST_SPRITE_SIZE.width,
    height: CHEST_SPRITE_SIZE.height,
  });
  if (!normalizedChest) return null;

  return {
    x: normalizedChest.x - CHEST_SENSOR_PADDING.x,
    y: normalizedChest.y - CHEST_SENSOR_PADDING.y,
    width: CHEST_SENSOR_SIZE.width,
    height: CHEST_SENSOR_SIZE.height,
  };
};

const buildChestCenterPoint = (chest = null) => {
  const x = Number(chest?.x);
  const y = Number(chest?.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return {
    x: x + CHEST_SPRITE_SIZE.width / 2,
    y: y + CHEST_SPRITE_SIZE.height / 2,
  };
};

const isChestFootprintWalkable = (chest = null, isPointBlocked = defaultBlockedCheck) => {
  const center = buildChestCenterPoint(chest);
  if (!center) return false;

  return CHEST_SAMPLE_OFFSETS.every(({ x, y }) =>
    !isPointBlocked({
      x: center.x + x,
      y: center.y + y,
    }),
  );
};

const isTooCloseToPoint = (chest = null, point = null, radius = 0) => {
  const center = buildChestCenterPoint(chest);
  const normalizedPoint = normalizePoint(point);
  if (!center || !normalizedPoint || radius <= 0) return false;

  return distanceSquared(center, normalizedPoint) < radius ** 2;
};

const buildCandidateChests = (bounds = WHISPERING_FOREST_STORY_PACK_BOUNDS) => {
  const candidates = [];
  for (
    let centerY = Number(bounds?.minY || 0);
    centerY <= Number(bounds?.maxY || 0);
    centerY += CHEST_CANDIDATE_STEP
  ) {
    for (
      let centerX = Number(bounds?.minX || 0);
      centerX <= Number(bounds?.maxX || 0);
      centerX += CHEST_CANDIDATE_STEP
    ) {
      candidates.push({
        x: Math.round(centerX - CHEST_SPRITE_SIZE.width / 2),
        y: Math.round(centerY - CHEST_SPRITE_SIZE.height / 2),
      });
    }
  }

  return candidates;
};

export const isWhisperingForestStoryPackChestMode = (mode = "") =>
  String(mode || "").trim().toLowerCase() === WHISPERING_FOREST_STORY_PACK_CHEST_MODE;

export const isWhisperingForestStoryPackChestRequest = (detail = {}) =>
  isWhisperingForestStoryPackChestMode(detail?.chestMode) &&
  Boolean(String(detail?.rewardCardId || detail?.rewardCard?.id || "").trim());

export const buildWhisperingForestStoryPackGrant = (rewardCard = {}) => {
  const rewardId = String(rewardCard?.id || "").trim();
  const rewardName = String(rewardCard?.name || "Infinity Card").trim();
  if (!rewardId || !rewardName) return null;

  return {
    kind: "infinityCard",
    id: rewardId,
    sourceId: rewardId,
    name: rewardName,
    imageUrl: String(rewardCard?.imageUrl || "").trim(),
    quantity: 1,
    stackable: true,
  };
};

export const buildWhisperingForestStoryChestRollout = ({
  random = Math.random,
  rewardPool = WHISPERING_FOREST_STORY_PACK_REWARD_POOL,
  bounds = WHISPERING_FOREST_STORY_PACK_BOUNDS,
  entrySpawn = null,
  exitRect = null,
  voidCavePortalRect = null,
  runeStonePosition = null,
  npcStartPoints = [],
  excludedRects = [],
  isPointBlocked = defaultBlockedCheck,
  idPrefix = "story-pack",
} = {}) => {
  const normalizedEntrySpawn = normalizePoint(entrySpawn);
  const normalizedRuneStonePosition = normalizePoint(runeStonePosition);
  const normalizedNpcStartPoints = npcStartPoints.map(normalizePoint).filter(Boolean);
  const blockedRects = [
    buildExpandedRect(exitRect, CHEST_ZONE_PADDING),
    buildExpandedRect(voidCavePortalRect, CHEST_ZONE_PADDING),
    ...excludedRects.map((rect) => buildExpandedRect(rect, CHEST_ZONE_PADDING)),
  ].filter(Boolean);

  const rewardCount = Math.min(
    randomIntInclusive(
      STORY_PACK_CHEST_MIN_COUNT,
      STORY_PACK_CHEST_MAX_COUNT,
      random,
    ),
    rewardPool.length,
  );
  const selectedRewards = shuffle(rewardPool, random).slice(0, rewardCount);
  const selectedChests = [];

  shuffle(buildCandidateChests(bounds), random).forEach((candidate) => {
    if (selectedChests.length >= selectedRewards.length) return;

    const chestRect = buildChestSensorRect(candidate);
    if (!chestRect || !isChestFootprintWalkable(candidate, isPointBlocked)) return;

    if (
      normalizedEntrySpawn &&
      isTooCloseToPoint(candidate, normalizedEntrySpawn, CHEST_ENTRY_EXCLUSION_RADIUS)
    ) {
      return;
    }

    if (
      normalizedRuneStonePosition &&
      isTooCloseToPoint(
        candidate,
        normalizedRuneStonePosition,
        CHEST_RUNE_STONE_EXCLUSION_RADIUS,
      )
    ) {
      return;
    }

    if (
      normalizedNpcStartPoints.some((point) =>
        isTooCloseToPoint(candidate, point, CHEST_NPC_EXCLUSION_RADIUS),
      )
    ) {
      return;
    }

    if (
      blockedRects.some(
        (blockedRect) =>
          rectsOverlap(chestRect, blockedRect) ||
          pointInsideRect(buildChestCenterPoint(candidate), blockedRect),
      )
    ) {
      return;
    }

    if (
      selectedChests.some((existingChest) => {
        const existingRect = buildChestSensorRect(existingChest);
        return (
          rectsOverlap(chestRect, existingRect) ||
          isTooCloseToPoint(
            candidate,
            buildChestCenterPoint(existingChest),
            STORY_PACK_CHEST_MIN_SPACING,
          )
        );
      })
    ) {
      return;
    }

    selectedChests.push(candidate);
  });

  return selectedChests.slice(0, selectedRewards.length).map((chest, index) => ({
    id: `${idPrefix}-${index + 1}-${Math.round(random() * 1e9).toString(36)}`,
    x: chest.x,
    y: chest.y,
    chestMode: WHISPERING_FOREST_STORY_PACK_CHEST_MODE,
    rewardCard: { ...selectedRewards[index] },
  }));
};

const cloneStoryChest = (chest = {}) => ({
  ...chest,
  rewardCard: chest.rewardCard ? { ...chest.rewardCard } : null,
  opened: Boolean(chest.opened),
});

export const getOrCreateWhisperingForestStoryChestVisit = (options = {}) => {
  if (!whisperingForestStoryChestVisit) {
    whisperingForestStoryChestVisit = buildWhisperingForestStoryChestRollout(
      options,
    ).map(cloneStoryChest);
  }

  return whisperingForestStoryChestVisit.map(cloneStoryChest);
};

export const markWhisperingForestStoryChestOpened = (chestId = "") => {
  const chest = whisperingForestStoryChestVisit?.find(
    (entry) => entry.id === chestId,
  );
  if (!chest) return false;

  chest.opened = true;
  return true;
};

export const clearWhisperingForestStoryChestVisit = () => {
  whisperingForestStoryChestVisit = null;
};
